import {
  useEffect, useReducer, useState,
} from 'react';
import { CookiesProvider } from 'react-cookie';
import { AES } from 'crypto-js';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import Header from 'components/Header';
import Footer from 'components/Footer';
import CookieConsentModal from 'components/CookieConsentModal';
import ErrorBoundary from 'components/ErrorBoundary';
import useUser from 'api/hooks/useUser';
import { useUserAgent } from 'hooks/useUserAgent';
import {
  AppContext, AppContextDispatch, appContext,
} from 'store/context';
import UseMobileApp from 'UseMobileApp';
import { appReducer } from 'store/reducers';
import { MobilePersister } from 'utils/queryClient';
import { Mixpanel } from 'mixpanel';
import SecureStorage, { STORAGE_MESSAGE_TYPE } from 'utils/localStorage';
import { infoToast } from 'utils/toast';
import useForceGreenLanguageAndCurrency from 'hooks/useForceGreenLanguage';

import Navigation from './Navigation';

import 'react-toastify/dist/ReactToastify.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'react-tooltip/dist/react-tooltip.css';
import 'react-international-phone/style.css';

const AppContent = () => {
  const [store, localDispatch] = useReducer(appReducer, appContext);
  const token = SecureStorage.getItemSync('token');
  const { data: user } = useUser();
  const [run, setRun] = useState(false);
  const [isSendAuthMessage, setIsSendAuthMessage] = useState(false);

  const reactNativeWebView = (window as any)?.ReactNativeWebView;

  async function handleEvent({ data: JSONmessage }: any) {
    if (!JSONmessage) {
      return;
    }

    const message = JSON.parse(JSONmessage);

    if (!message) {
      return;
    }

    if (message.type === STORAGE_MESSAGE_TYPE) {
      return SecureStorage.handleReactNativeResponse(
        message.messageId,
        message,
      );
    }

    if (message.type === 'readQuery') {
      return MobilePersister.deserialize(message.data);
    }

    if (message.type === 'ssoToken') {
      // eslint-disable-next-line object-property-newline
      return localDispatch({
        type: 'ssoAccessToken',
        payload: message.accessToken,
      });
    }

    if (message?.type !== 'netStatus') {
      return;
    }

    const prevNetStatus = localStorage.getItem('netStatus');

    if (message?.type === 'netStatus') {
      localStorage.setItem('netStatus', message.data);
    }

    if (message.trackingAllowed == 'false') {
      Mixpanel.disabled();
    }

    if (message.xBiometricToken) {
      SecureStorage.setItem('xBiometricToken', message.xBiometricToken, {
        secure: true,
      });
    }

    if (message.accessToken) {
      SecureStorage.setItem('token', message.accessToken, {
        secure: true,
      });
      localStorage.setItem('mfa', 'true');
    }

    if (message.refreshToken) {
      SecureStorage.setItem('refreshToken', message.refreshToken, {
        secure: true,
      });
    }

    if (message.userData) {
      SecureStorage.setItem('userData', message.userData, {
        secure: true,
      });
    }

    if (!run) {
      setRun(true);
    }

    const isFirstLaunchWebViewDone = localStorage.getItem(
      'isFirstLaunchWebViewDone',
    );

    if (!isFirstLaunchWebViewDone) {
      localStorage.setItem('isFirstLaunchWebViewDone', 'true');

      // Instead of hard reload, show toast and refresh data
      if (message.accessToken) {
        infoToast('✅ Authentication successful');
        // You can refresh specific data instead of whole page
        // window.location.reload();
      }
    }

    // Handle network status changes gracefully
    if (prevNetStatus && prevNetStatus !== message.data) {
      if (message.data === 'online') {
        infoToast('🌐 Connection restored');
      } else {
        infoToast('📱 You are now offline');
      }

      // Instead of hard reload, you can refresh data or update UI state
      // window.location.reload();
    }
  }

  const { isMobile } = useUserAgent();

  useForceGreenLanguageAndCurrency();

  useEffect(() => {
    if (!user) {
      return;
    }

    const isOnline = localStorage.getItem('netStatus') !== 'offline';

    if (user && isOnline) {
      const hash = AES.encrypt(
        JSON.stringify(user),
        import.meta.env.VITE_APP_USER_DATA_SECRET,
      );
      SecureStorage.setItem('userData', hash.toString(), {
        secure: true,
      });
    }

    if (
      reactNativeWebView &&
      !isSendAuthMessage &&
      localStorage.getItem('netStatus') !== 'offline' &&
      user &&
      token &&
      (!user.isTwoFAEnabled || localStorage.getItem('mfa'))
    ) {
      reactNativeWebView.postMessage(
        JSON.stringify({
          auth: true,
          accessToken: token,
          refreshToken: SecureStorage.getItemSync('refreshToken'),
          userData: SecureStorage.getItemSync('userData'),
        }),
      );

      setIsSendAuthMessage(true);
    }
  }, [user, token, reactNativeWebView, isSendAuthMessage]);

  useEffect(() => {
    if (!reactNativeWebView) {
      return setRun(true);
    }

    const clearSensitiveData = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
    };
    clearSensitiveData();

    const platform = localStorage.getItem('_platform');

    if (platform) {
      reactNativeWebView.postMessage(
        JSON.stringify({
          type: 'webReady',
          value: true,
        }),
      );
    }

    if (platform === 'ios') {
      window.addEventListener('message', handleEvent);

      return () => window.removeEventListener('message', handleEvent);
    }

    if (platform === 'android') {
      document.addEventListener('message', handleEvent);

      return () => document.removeEventListener('message', handleEvent);
    }
  }, []);

  if (!run) {
    return null;
  }

  if (isMobile && !reactNativeWebView) {
    return <UseMobileApp />;
  }

  return (
    <CookiesProvider>
      <AppContext.Provider value={store}>
        <AppContextDispatch.Provider value={localDispatch}>
          <div className="min-h-screen flex-col flex">
            {reactNativeWebView ? (
              <HashRouter>
                <ToastContainer />
                <CookieConsentModal />
                {token && user && <Header />}
                <div className="flex-1">
                  <Navigation />
                </div>
                <Footer />
              </HashRouter>
            ) : (
              <BrowserRouter>
                <ToastContainer />
                <CookieConsentModal />
                {token && user && <Header />}
                <div className="flex-1">
                  <Navigation />
                </div>
                <Footer />
              </BrowserRouter>
            )}
          </div>
        </AppContextDispatch.Provider>
      </AppContext.Provider>
    </CookiesProvider>
  );
};

const App = () => (
  <ErrorBoundary>
    <AppContent />
  </ErrorBoundary>
);

export default App;
